import React from 'react';
import { Helmet } from 'react-helmet-async';

interface PageMetadataProps {
  title?: string;
  description?: string;
  canonicalUrl?: string;
}

/**
 * PageMetadata component for setting page-specific metadata
 * 
 * Usage example:
 * ```jsx
 * <PageMetadata 
 *   title="Afforda | Your Page Title" 
 *   description="Your page description for SEO and social sharing" 
 *   canonicalUrl="https://example.com/optional-canonical-url" 
 * />
 * ```
 * 
 * If canonicalUrl is not provided, it will use the current URL
 */
const PageMetadata: React.FC<PageMetadataProps> = ({ 
  title, 
  description, 
  canonicalUrl 
}) => {
  // Simplified implementation to avoid infinite loops
  // We're only using direct Helmet rendering now
  const resolvedCanonicalUrl = canonicalUrl || window.location.href;
  
  return (
    <Helmet>
      {title && <title key="title">{title}</title>}
      {description && (
        <meta key="description" name="description" content={description} />
      )}
      {resolvedCanonicalUrl && (
        <link key="canonical" rel="canonical" href={resolvedCanonicalUrl} />
      )}
    </Helmet>
  );
};

export default PageMetadata;
